<template>
  <v-container fluid class="list-page list-page--embedded advance-credit-list">
    <Create :CustomerId="CustomerId" :createDialog="createDialog" @close="createDialog = false" @submit="getList()">
    </Create>
    <AttachInvoice :selectedAdvancePayment="selectedAdvancePayment" :attachInvoiceDialog="attachInvoiceDialog" @close="closeAttachInvoiceDialog()" @submit="allocationSaved">
    </AttachInvoice>
    <v-row class="list-metrics advance-credit-list__metrics">
      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-receipt-text-outline</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ advancePayments.length }}</div>
            <div class="list-metric__label">Advance payments</div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-cash-check</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ fullTotalAmount | currency }}</div>
            <div class="list-metric__label">Payments received</div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-cash-clock</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ fullTotalBalance | currency }}</div>
            <div class="list-metric__label">Available tuition credit</div>
          </div>
        </div>
      </v-col>

      <v-col
        v-if="checkRightStatus(31) && CustomerId"
        cols="12"
        sm="4"
        md="3"
        class="advance-credit-list__metric-action"
      >
        <v-btn color="primary" depressed @click="add">
          <v-icon left>mdi-plus</v-icon>
          Receive advance
        </v-btn>
      </v-col>
    </v-row>
    <v-card :loading="loading" class="list-card" elevation="0">
      <div class="list-toolbar">
        <v-text-field
          v-model="search"
          class="list-toolbar__search"
          outlined
          prepend-inner-icon="mdi-magnify"
          dense
          background-color="#F8FAF8"
          placeholder="Search receipt number, note, method, or amount"
          single-line
          hide-details
          clearable
        />
        <div class="list-toolbar__actions">
          <div v-if="!showAll" class="list-toolbar__control">
            <DateTimePicker defaultRange="thisYear" :range="true" v-model="dateTime" @submit="getList()" />
          </div>
          <v-btn
            icon
            outlined
            color="primary"
            :loading="loading"
            aria-label="Refresh advance payments"
            @click="getList"
          >
            <v-icon size="19">mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>
      <v-card-text class="pa-0">
        <v-data-table :headers="headers" :items="advancePayments" :search="search" :loading="loading"
          @current-items="updateTotal" :sort-by="sortBy" sort-desc :page.sync="page" :expanded.sync="expanded"
          show-expand :items-per-page="itemsPerPage" class="list-table advance-credit-list__table">
          <template v-slot:item="{ item, isExpanded, expand }">
            <tr class="list-table-row">
              <td class="text-left pointer">
                <v-btn @click="expand(true)" icon v-if="!isExpanded">
                  <v-icon>mdi-chevron-down</v-icon>
                </v-btn>
                <v-btn @click="expand(false)" icon v-if="isExpanded">
                  <v-icon>mdi-chevron-up</v-icon>
                </v-btn>
              </td>
              <td class="text-left">
                {{ item.createdAt | moment("MM/DD/YYYY hh:mm A") }}
              </td>
              <td class="text-left">
                <strong class="advance-credit-list__receipt">#{{ item.localId }}</strong>
                <v-chip color="pink" dark small class="ml-2" v-if="item.donation">Donation</v-chip>
              </td>
              <td class="text-left">
                {{ item.note }}
              </td>
              <td class="text-left">
                <v-chip small outlined color="primary">{{ item.paymentMethod || "Manual" }}</v-chip>
              </td>
              <td class="text-right">
                <strong>{{ item.amount | currency }}</strong>
              </td>
              <td class="text-right">
                <strong class="advance-credit-list__balance">{{ item.balance | currency }}</strong>
              </td>
              <td class="justify-end align-center text-right">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon aria-label="Advance payment actions">
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <v-list class="menu-button">
                    <v-list-item @click="attachInvoice(item)">
                      <v-list-item-icon><v-icon size="19">mdi-link-variant</v-icon></v-list-item-icon>
                      <v-list-item-title>Attach invoice</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="item.amount === item.balance" @click="deletePayment(item)">
                      <v-list-item-icon><v-icon size="19" color="error">mdi-delete-outline</v-icon></v-list-item-icon>
                      <v-list-item-title class="error--text">Delete payment</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
              <v-card color="#F3F8FC" text class="elevation-0">
                <v-card-text class="title">
                  <AdvancePaymentDetail :AdvancePaymentId="item.id" ></AdvancePaymentDetail>
                </v-card-text>
              </v-card>
            </td>
          </template>
          <v-alert slot="no-results" :value="true" color="error" icon="warning">Your search for "{{ search }}" found no
            results.</v-alert>

          <template v-slot:no-data>
            <div class="list-empty-state">
              <v-icon size="44" color="grey lighten-1">mdi-cash-clock</v-icon>
              <strong>No advance payments found</strong>
              <span>Advance tuition payments will appear here after they are received.</span>
              <v-btn v-if="checkRightStatus(31) && CustomerId" color="primary" depressed small @click="add">
                <v-icon left small>mdi-plus</v-icon>
                Receive advance
              </v-btn>
            </div>
          </template>

          <template v-slot:body.append="{ headers }">
            <tr v-if="advancePayments.length" class="advance-credit-list__subtotal">
              <td :colspan="headers.length - 3" class="text-right font-weight-bold">
                List Total
              </td>
              <td class="text-right font-weight-bold">
                {{ totalAmount | currency }}
              </td>
              <td class="text-right font-weight-bold">
                {{ totalBalance | currency }}
              </td>
              <td></td>
            </tr>
            <tr v-if="advancePayments.length" class="advance-credit-list__total">
              <td :colspan="headers.length - 3" class="text-right font-weight-bold">
                Result Total
              </td>
              <td class="text-right font-weight-bold">
                {{ fullTotalAmount | currency }}
              </td>
              <td class="text-right font-weight-bold">
                {{ fullTotalBalance | currency }}
              </td>
              <td></td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import advancePaymentService from "../service";
const DateTimePicker = () => import("@/components/DateTimePicker");
import AdvancePaymentDetail from "./DetailList";
import { mapGetters } from "vuex";

const Create = () => import("./Create");
const AttachInvoice = () => import("./AttachInvoice");

export default {
  props: {
    CustomerId: {
      default: null,
    },
    showAll: {
      default: false,
    },
  },
  name: "advance-payment-list",
  data() {
    return {
      fullTotalAmount: 0,
      fullTotalBalance: 0,
      totalAmount: 0,
      totalBalance: 0,
      expanded: [],
      dateTime: {
        start: this.$moment()
          .startOf("year")
          .toISOString(),
        end: this.$moment()
          .endOf("year")
          .toISOString(),
      },
      createDialog: false,
      attachInvoiceDialog: false,
      selectedAdvancePayment: null,
      headers: [],
      advancePayments: [],
      search: null,
      loading: true,
      page: 1,
      sortBy: "localId",
      pageCount: 0,
      itemsPerPage: 10,
    };
  },
  created() {
    this.$events.listen("advancePaymentListEvent", () => this.getList());
    this.loadHeader();
  },
  beforeDestroy() {
    this.$events.remove("advancePaymentListEvent");
  },
  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),
  },
  watch: {
    CustomerId: {
      immediate: true,
      handler() {
        this.getList();
      },
    },
  },
  components: {
    DateTimePicker,
    Create,
    AttachInvoice,
    AdvancePaymentDetail,
  },
  methods: {
    updateTotal(val) {
      this.totalAmount = 0;
      this.totalBalance = 0;
      val.map((row) => {
        this.totalAmount += row.amount;
        this.totalBalance += row.balance;
      });
    },
    add() {
      this.createDialog = true;
    },
    attachInvoice(row) {
      this.selectedAdvancePayment = row
      this.attachInvoiceDialog = true
    },
    loadHeader() {
      this.headers = [];
      this.headers.push({
        text: this.$t("labels.date"),
        align: "left",
        value: "date",
        sortable: true,
      });

      this.headers.push({
        text: "Receipt Number",
        align: "left",
        value: "localId",
        sortable: true,
      });

      this.headers.push({
        text: "Note",
        align: "left",
        value: "note",
        sortable: true,
      });

      this.headers.push({
        text: "Received by",
        align: "left",
        value: "paymentMethod",
        sortable: true,
      });

      this.headers.push({
        text: "Amount",
        value: "amount",
        align: "right",
        sortable: true,
      });

      this.headers.push({
        text: "Balance",
        value: "balance",
        align: "right",
        sortable: true,
      });

      this.headers.push({
        text: "",
        value: "action",
        sortable: false,
      });
    },
    closeAttachInvoiceDialog(){
      this.selectedAdvancePayment = null
      this.attachInvoiceDialog = false
    },
    allocationSaved() {
      return this.getList();
    },
    getList() {
      this.loading = true;
      let where = {};
      if (!this.showAll) {
        where = {
          start: this.dateTime.start,
          end: this.dateTime.end,
        };
      }
      if (this.CustomerId) {
        where.CustomerId = this.CustomerId;
      }

      return advancePaymentService.getAll(where).then((response) => {
        this.loading = false;
        this.advancePayments = response.data;
        this.fullTotalAmount = 0;
        this.fullTotalBalance = 0;
        if(this.advancePayments.length){
          this.advancePayments.map((row) => {
            this.fullTotalAmount += row.amount;
            this.fullTotalBalance += row.balance;
          });
        }
        return response;
      });
    },
    deletePayment(payment) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          advancePaymentService.delete(payment.id).then((response) => {
            if (response.data) {
              this.$swal
                .mixin({
                  toast: true,
                  position: "top-end",
                  showConfirmButton: false,
                  timer: 3000,
                })
                .fire({
                  icon: "success",
                  title: "Payment has been deleted.",
                });
              this.getList();
            }
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.advance-credit-list__metric-action { display: flex; align-items: center; justify-content: flex-end; }
.advance-credit-list__metric-action .v-btn { min-height: 46px; border-radius: 9px; text-transform: none; letter-spacing: 0; }
.advance-credit-list__receipt { color: #2f8738; }
.advance-credit-list__balance { color: #2f8738; }
.advance-credit-list__subtotal { background: #f7faf7; }
.advance-credit-list__total { background: #eef7ef; }
@media (max-width: 600px) { .advance-credit-list__metric-action { justify-content: stretch; } .advance-credit-list__metric-action .v-btn { width: 100%; } }
</style>
